import request from "../../../request";

interface AgrPolicyData {
  [propertyTicker: string]: {
    childPropertyTickers?: string[];
    [key: string]: unknown;
  };
}

interface CloneEntity {
  childPropertyTickers: string[];
  [key: string]: unknown;
}

interface ExcelResult {
  data: Blob;
  filename: string;
}

function createClient(baseURL: string) {
  return request.create({ baseURL });
}

export async function fetchAgrPolicyData(
  baseURL: string,
  propertyTicker: string,
  policyTicker: string,
  signal?: AbortSignal
): Promise<AgrPolicyData> {
  const httpClient = createClient(baseURL);
  const response = await httpClient.get(
    `/api/backoffice/aggregator/${propertyTicker}/cancellationpolicies/${policyTicker}`,
    { signal }
  );
  return response.data || {};
}

export async function saveAgrPolicy(
  baseURL: string,
  propertyTicker: string,
  policyTicker: string,
  chainBody: Record<string, unknown>,
  isEdit: boolean
): Promise<void> {
  const httpClient = createClient(baseURL);
  if (isEdit) {
    await httpClient.put(
      `/api/backoffice/aggregator/${propertyTicker}/cancellationpolicies/${policyTicker}`,
      chainBody
    );
  } else {
    await httpClient.post(
      `/api/backoffice/aggregator/${propertyTicker}/cancellationpolicies`,
      chainBody
    );
  }
}

export async function propagateAgrReorder(
  baseURL: string,
  propertyTicker: string,
  id: string,
  afterId?: string
): Promise<void> {
  const httpClient = createClient(baseURL);
  const path =
    `/api/backoffice/aggregator/${propertyTicker}/cancellationpolicies/${id}/reorder` +
    (afterId ? `?afterId=${encodeURIComponent(afterId)}` : "");
  await httpClient.put(path);
}

export async function cloneAgrPolicy(
  baseURL: string,
  propertyTicker: string,
  policyTicker: string
): Promise<CloneEntity> {
  const httpClient = createClient(baseURL);
  const [individualRes, agrRes] = await Promise.all([
    httpClient.get(`/api/backoffice/${propertyTicker}/cancellationpolicies/${policyTicker}`),
    httpClient.get(
      `/api/backoffice/aggregator/${propertyTicker}/cancellationpolicies/${policyTicker}`
    ),
  ]);
  const flatEntity = individualRes.data;
  const parentEntry = agrRes.data?.[propertyTicker];
  const childPropertyTickers: string[] = parentEntry?.childPropertyTickers || [];
  const cloneEntity = { ...flatEntity, childPropertyTickers };
  delete cloneEntity.id;
  delete cloneEntity.ticker;
  return cloneEntity;
}

export async function downloadPoliciesExcel(
  baseURL: string,
  propertyTicker: string
): Promise<ExcelResult> {
  const httpClient = createClient(baseURL);
  const response = await httpClient.get(
    `/api/backoffice/${propertyTicker}/cancellationpolicies/xlsx`,
    { responseType: "blob" }
  );
  const disposition: string = response.headers["content-disposition"] || "";
  const filename = disposition.includes("=")
    ? disposition.split(";")[1].trim().split("=")[1].replace(/"/g, "")
    : "cancellationpolicies.xlsx";
  return { data: response.data, filename };
}
